<template>
  <div
    class="flex items-center bg-default-red border mx-0 py-2 px-3.25 pl-2.75 md:px-7.25 shadow-sm h-18.5"
    :class="{
      'bg-indigo-410 border-indigo-410 border-b-slate-50/10': !route.meta.isDarkBackground,
      'border-default border-b-cyan-30': route.meta.isDarkBackground,
    }"
  >
    <div class="w-full items-center justify-between">
      <div class="w-full flex basis-auto items-center">
        <div
          class="transition-all duration-300 flex flex-1 flex-row ml-0 md:ml-auto items-center mt-0 text-slate-50 gap-7.25 md:gap-7.5"
          :class="[!isSearchOpen ? 'w-0 overflow-hidden sm:flex' : 'w-full']"
        >
          <div class="relative inline-block lg:hidden text-white">
            <div class="flex items-center">
              <MenuIcon v-if="!isSBOpen" class="cursor-pointer h-6 w-6" @click="handleMenuClick" />
              <MenuAlt1Icon v-else class="cursor-pointer h-6 w-6" @click="handleMenuClick" />
            </div>
          </div>
          
        </div>
        <div
          class="flex flex-none flex-row ml-auto md:ml-0 pl-7.5"
          :class="[!isSearchOpen ? 'hidden sm:flex md:flex lg:flex' : '']"
        >

          <LanguageDropdown/>  

          <el-dropdown placement="bottom-end" trigger="click" popper-class="profile-popper">
            <div class="flex flex-row items-center gap-1 md:gap-2 mb-1">
              <div class="pt-1">
                <el-link :underline="false">
                  
                </el-link>
              </div>
              <div class="">
                <h4>
                  <el-link
                    :underline="false"
                    class="text-lg font-semibold text-slate-50 hover:text-slate-300 pt-1"
                    >{{ userData.username || 'NoUser' }}</el-link
                  >
                </h4>
              </div>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item class="m-0 hover:bg-white">
                  <div class="flex items-center w-full pt-1 cursor-default">
                    <h5 class="truncate m-0 text-primary-dark">{{ welcomeText }}</h5>
                  </div>
                </el-dropdown-item>
                <el-dropdown-item class="mx-0 my-2 hover:bg-slate-100 text-zinc-800">
                  <router-link to="/profile/detail" class="flex flex-row items-center w-full h-6 mr-auto">
                    <div class="flex w-auto">
                      <el-icon :size="22" class="cursor-pointer w-5 h-6">
                        <UserFilled />
                      </el-icon>
                    </div>
                    <div class="basis-0 grow w-full ml-4">
                      <span class="mb-0 text-sm font-normal">{{ myProfile }}</span>
                    </div>
                  </router-link>
                </el-dropdown-item>
                <el-dropdown-item class="mx-0 my-2 hover:bg-slate-100 text-zinc-800">
                  <router-link to="/profile/custom-settings" class="flex flex-row items-center w-full h-6 mr-auto">
                    <div class="flex w-auto">
                      <el-icon :size="22" class="cursor-pointer w-5 h-6">
                        <Tools />
                      </el-icon>
                    </div>
                    <div class="basis-0 grow w-full ml-4">
                      <span class="mb-0 text-sm font-normal">{{ customSettings }}</span>
                    </div>
                  </router-link>
                </el-dropdown-item>

                <el-dropdown-item
                  divided
                  class="mx-0 mt-2 hover:bg-slate-100 text-zinc-800"
                  @click="handleLogoutClick"
                >
                  <div class="flex flex-row items-center w-full h-6">
                    <div class="flex w-auto">
                      <el-icon :size="20" class="cursor-pointer w-5 h-6">
                        <CircleClose />
                      </el-icon>
                    </div>
                    <div class="basis-0 grow w-full ml-4">
                      <span class="mb-0 text-0.95 font-normal">{{ logout }}</span>
                    </div>
                  </div>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import {
  Search,
  BellFilled,
  Menu,
  LocationFilled,
  List,
  GoodsFilled,
  UserFilled,
  Tools,
  CircleClose,
} from '@element-plus/icons-vue'
import { MenuIcon, MenuAlt1Icon, SearchIcon, SearchCircleIcon } from '@heroicons/vue/outline'
import useStore from 'store'
import { useState } from 'modules/auth/store/state'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LanguageDropdown from '@/utils/LanguageDropdown.vue'

export default defineComponent({
  name: 'DefaultNav',
  components: {
    Search,
    BellFilled,
    Menu,
    LocationFilled,
    List,
    GoodsFilled,
    UserFilled,
    Tools,
    CircleClose,
    SearchIcon,
    MenuIcon,
    MenuAlt1Icon,
    SearchCircleIcon,
    LanguageDropdown,
  },
  setup() {
    const route: any = useRoute()
    const store = useStore()
    const authState = useState()
    const userData = authState.user

    const { t, locale } = useI18n()

    const isPagesMenuOpen = ref(false)
    const isSideMenuOpen = ref(false)
    const isSearchOpen = ref(true)
    const clickIconBell = ref(false)
    const clickIconMenu = ref(false)
    const togglePagesMenu = () => {
      isSideMenuOpen.value = !isSideMenuOpen.value
    }
    const closeSideMenu = () => {
      isSideMenuOpen.value = false
    }

    const isSBPin = computed(() => store.dashboard.isSBPin)
    const isSBOpen = computed(() => store.dashboard.isSBOpen)

    const handleMenuClick = () => {
      store.dashboard.toggleMenu()
    }

    const handleLogoutClick = () => {
      store.auth.actLogout()
    }

    const setSearchOpen = (v: boolean) => (isSearchOpen.value = v)

    const welcomeText = t('WELCOME!')
    const logout = t('Logout')
    const myProfile = t('My profile')
    const customSettings = t('Custom settings')

    return {
      userData,
      isPagesMenuOpen,
      isSideMenuOpen,
      isSearchOpen,
      isSBPin,
      isSBOpen,
      clickIconBell,
      clickIconMenu,
      route,
      handleLogoutClick,
      handleMenuClick,
      setSearchOpen,
      togglePagesMenu,
      closeSideMenu,
      welcomeText,
      logout,
      myProfile,
      customSettings,
    }
  },
})
</script>
